import {
  actionColumn,
  Button,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  TableReviewLayout,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FormattedDate } from "src/components/FormattedDate";
import { HbLoadingSpinner } from "src/components/HbLoadingSpinner";
import { PlanPackageIncomingSlotStatus } from "src/generated/graphqlTypes";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { createPlanPackageTakeoffUrl } from "src/RouteUrls";
import { groupBy } from "src/utils/utils";
import { ReviewSlotRequests_IncomingSlotFragment, useReviewSlotRequestsQuery } from "./ReviewSlotRequests.generated";
import { useSlotReviewPanel } from "./SlotReviewPanel";

type HeaderRow = { kind: "header" };
type GroupByRow = { kind: "groupBy"; data: string };
type SlotRow = { kind: "slot"; data: ReviewSlotRequests_IncomingSlotFragment };
type Rows = HeaderRow | GroupByRow | SlotRow;

export function ReviewSlotRequestsPage() {
  const { planPackageId, planPackageVersionId } = useParams<PlanPackageDetailsParams>();
  const history = useHistory();
  const { data, loading } = useReviewSlotRequestsQuery({ variables: { planPackageId } });
  const [selectedSlot, setSelectedSlot] = useState<ReviewSlotRequests_IncomingSlotFragment | null>(null);
  const panelContent = useSlotReviewPanel(
    selectedSlot,
    () => setSelectedSlot(null),
    planPackageId,
    planPackageVersionId,
  );

  const rows = useMemo(
    () =>
      createRows(
        data?.planPackage.incomingSlots.filter((s) => s.status.code === PlanPackageIncomingSlotStatus.Pending) ?? [],
      ),
    [data],
  );
  const columns = useMemo(() => createColumns(setSelectedSlot), [setSelectedSlot]);

  const takeoffUrl = createPlanPackageTakeoffUrl(planPackageId, planPackageVersionId);

  return (
    <TableReviewLayout
      breadCrumb={{ href: takeoffUrl, label: data?.planPackage.name ?? "" }}
      pageTitle="Review slot requests"
      description={
        <div>
          Review and manage slot change requests from design packages.
          <br />
          Accepting a request will add it to Takeoff.
          <br />
          If you reject a request, please notify the interior designer and include a reason.
        </div>
      }
      closeAction={() => history.push(takeoffUrl)}
      tableProps={{ columns, rows, activeRowId: selectedSlot ? `slot_${selectedSlot.id}` : undefined }}
      emptyState={loading ? <HbLoadingSpinner /> : <AllSetEmptyState />}
      panelContent={panelContent}
      onPanelClose={() => setSelectedSlot(null)}
    />
  );
}

function createRows(pendingSlots: ReviewSlotRequests_IncomingSlotFragment[]): GridDataRow<Rows>[] {
  const sorted = [...pendingSlots].sortBy((s) => s.designPackagePlaceholder.item.costCode.number);

  const groups = groupBy(sorted, (s) => s.designPackagePlaceholder.item.costCode.displayName);

  return [
    { kind: "header", id: "header", data: undefined },
    ...Object.entries(groups).map(([label, slots]) => ({
      kind: "groupBy" as const,
      id: label,
      data: label,
      children: slots.map((slot) => ({ kind: "slot" as const, id: slot.id, data: slot })),
    })),
  ];
}

function createColumns(setSelectedSlot: (slot: ReviewSlotRequests_IncomingSlotFragment) => void): GridColumn<Rows>[] {
  return [
    column<Rows>({
      header: "Code",
      groupBy: (label) => ({
        colspan: 7, // minus the actionCol to give a left border across all cols including this row
        content: <div css={Css.xsSb.$}>{label}</div>,
      }),
      slot: emptyCell,
      w: "65px",
    }),
    column<Rows>({
      header: "Item code",
      groupBy: emptyCell,
      slot: ({ designPackagePlaceholder: p }) => p.item.fullCode,
      w: "96px",
    }),
    column<Rows>({ header: "Name", groupBy: emptyCell, slot: ({ designPackagePlaceholder: p }) => p.name, w: 2 }),
    column<Rows>({
      header: "Location",
      groupBy: emptyCell,
      slot: (slotRequest) => slotRequest.planPackageRpl.location.name,
      w: 1.5,
    }),
    column<Rows>({
      header: "Feature",
      groupBy: emptyCell,
      slot: ({ designPackagePlaceholder: p }) => p.location.feature?.displayLocationPath ?? "",
      w: 1.5,
    }),
    column<Rows>({ header: "Designer name", groupBy: emptyCell, slot: (s) => s.createdBy.name, w: 1.5 }),
    column<Rows>({
      header: "Request date",
      groupBy: emptyCell,
      slot: (s) => <FormattedDate date={s.createdAt} dateFormatStyle="short" />,
      mw: "120px",
    }),
    actionColumn<Rows>({
      header: emptyCell,
      groupBy: emptyCell,
      slot: (s) => <Button label="View" variant="secondaryBlack" size="sm" onClick={() => setSelectedSlot(s)} />,
      w: "80px",
      border: "left",
      sticky: "right",
    }),
  ];
}

function AllSetEmptyState() {
  return (
    <div css={Css.df.fdc.aic.tac.$}>
      <div css={Css.p6.$}>
        <img src={"/images/review-complete.svg"} alt="All set!" />
      </div>
      <div>
        <p css={Css.xl.fw6.mb1.$}>All set!</p>
        <p css={Css.md.gray600.$}>You have reviewed all slot requests.</p>
      </div>
    </div>
  );
}
